/**
 * Налаштування контролю підписання первинки на картці організації (0429).
 *
 * 2026-08-19 (зауваження власника):
 * 1. Галочка БІЛЬШЕ НЕ зберігається сама по собі — тільки кнопкою
 *    «зберегти» картки організації (батько викликає ref.flush()).
 * 2. Попап винесено ЗА межі <label>: раніше він жив усередині лейбла
 *    галочки, і контейнер глушив кліки preventDefault'ом (щоб клік не
 *    перемикав чекбокс лейбла) — через це радіо «паперовий оригінал»
 *    у попапі взагалі не перемикалось.
 * Попап зберігає список/радіо СВОЄЮ кнопкою «зберегти» (як і було).
 *
 * ч.1060 (вимога власника): відкладена галочка живе у СЛОТІ КАРТКИ, а не у
 * компоненті — при перемиканні вкладки вона більше не скидається, потрапляє
 * у чернетку картки, а «зберегти» картки зберігає її з будь-якої вкладки
 * (saveSignControlPending; раніше — ref.flush(), який після перемикання
 * вкладки вже нічого не бачив).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { signControl, type SignControlSettings as Settings } from '@/api/signControl';
import { useDraftSlot } from '@/shared/hooks/draftSlots';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';

const EXTRA_NAMES: Record<string, string> = {
  'ref.contracts': 'Договори з контрагентами',
};

const typeName = (t: string) =>
  DOC_TABLE_NAMES_UK[t] ?? EXTRA_NAMES[t] ?? t;

/** Ключ слота картки з відкладеною галочкою (null — без змін). */
export const SIGN_CONTROL_PENDING_SLOT = 'signControl.pendingEnabled';

/**
 * Зберегти відкладену зміну галочки — з кнопки «зберегти» картки
 * організації, незалежно від того, яка вкладка відкрита.
 */
export async function saveSignControlPending(
  qc: QueryClient, orgId: string, pending: boolean | null | undefined,
): Promise<void> {
  if (pending === null || pending === undefined) return;
  const s = await qc.fetchQuery({
    queryKey: ['sign-control-settings', orgId],
    queryFn: () => signControl.settings(orgId),
  });
  if (pending === s.enabled) return;
  await signControl.saveSettings(orgId, {
    enabled: pending, paper_original: s.paper_original, types: s.types,
  });
  qc.invalidateQueries({ queryKey: ['sign-control-settings', orgId] });
  qc.invalidateQueries({ queryKey: ['sign-control-statuses'] });
  qc.invalidateQueries({ queryKey: ['orgs'] });
}

export default function SignControlSettings({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['sign-control-settings', orgId],
    queryFn: () => signControl.settings(orgId),
  });
  // Відкладена зміна галочки (null = без змін, показуємо серверне значення).
  // ч.1060: у слоті картки — переживає перемикання вкладок і вихід зі сторінки.
  const [pendingEnabled, setPendingEnabled] = useDraftSlot<boolean | null>(
    SIGN_CONTROL_PENDING_SLOT, null);
  const [popup, setPopup] = useState<{
    paper: boolean; types: Set<string> } | null>(null);
  const save = useMutation({
    mutationFn: (body: { enabled: boolean; paper_original: boolean; types: string[] }) =>
      signControl.saveSettings(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sign-control-settings', orgId] });
      qc.invalidateQueries({ queryKey: ['sign-control-statuses'] });
      qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });


  // НЕ ховаємо галочку під час завантаження/помилки (скарга власника
  // 2026-08-17 «потерял вывод поля») — показуємо disabled-стан.
  if (!q.data) {
    return (
      <label style={{ display: 'block', marginTop: '0.5rem', color: 'var(--muted)' }}>
        <input type="checkbox" disabled checked={false}
          style={{ width: 'auto', marginRight: '0.4rem' }} />
        Контроль підписання первинних документів
        <small style={{ marginLeft: 6 }}>
          {q.isError ? '(не вдалося завантажити — оновіть сторінку)' : '(завантаження…)'}
        </small>
      </label>
    );
  }
  const s: Settings = q.data;
  const shownEnabled = pendingEnabled ?? s.enabled;

  return (
    <>
      <label style={{ cursor: 'pointer', display: 'block', marginTop: '0.5rem' }}>
        <input
          type="checkbox"
          checked={shownEnabled}
          onChange={(e) => setPendingEnabled(
            e.target.checked === s.enabled ? null : e.target.checked)}
          style={{ width: 'auto', marginRight: '0.4rem' }}
        />
        Контроль підписання первинних документів
        {shownEnabled && (
          <a style={{ marginLeft: '0.6rem', fontSize: '0.82rem', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              setPopup({ paper: s.paper_original, types: new Set(s.types) });
            }}>
            список документів контролю
          </a>
        )}
      </label>

      {popup && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setPopup(null)}>
          {/* ч.781: шапка і плашка кнопок нерухомі, скролиться лише перелік. */}
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(620px, 94vw)',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
            cursor: 'default', fontWeight: 400 }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, padding: '1rem 1rem 0.6rem' }}>Список документів контролю</h3>
            <div style={{ overflowY: 'auto', flex: '1 1 auto', padding: '0 1rem' }}>

            <div style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 6,
              padding: '0.5rem 0.7rem', marginBottom: '0.7rem' }}>
              <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" checked={!popup.paper}
                  onChange={() => setPopup({ ...popup, paper: false })} />
                не використовувати кнопку «паперовий оригінал» (рекомендовано)
              </label>
              <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" checked={popup.paper}
                  onChange={() => setPopup({ ...popup, paper: true })} />
                використовувати кнопку «паперовий оригінал»
              </label>
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.25rem' }}>
                «Паперовий оригінал» знімає документ з контролю без скану —
                лише зі слів користувача. Надійніше вимагати скан або ЕЦП.
              </small>
            </div>

            {s.catalog.map(({ group, types }) => (
              <div key={group} style={{ marginBottom: '0.55rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.2rem' }}>
                  {group}
                </div>
                <div style={{ display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.1rem' }}>
                  {types.map((t) => (
                    <label key={t} style={{ display: 'flex', gap: '0.35rem',
                      alignItems: 'center', cursor: 'pointer', fontSize: '0.83rem' }}>
                      <input type="checkbox" checked={popup.types.has(t)}
                        onChange={(e) => {
                          const next = new Set(popup.types);
                          if (e.target.checked) next.add(t); else next.delete(t);
                          setPopup({ ...popup, types: next });
                        }} />
                      {typeName(t)}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end',
              padding: '0.7rem 1rem', borderTop: '1px solid var(--border, #e2e8f0)',
              background: 'var(--bg-panel)', flex: '0 0 auto' }}>
              <button onClick={() => setPopup(null)}>скасувати</button>
              <button onClick={() => setPopup({
                paper: popup.paper, types: new Set(s.default_types) })}>
                за замовчуванням
              </button>
              <button className="primary" disabled={save.isPending}
                onClick={() => {
                  save.mutate({ enabled: true, paper_original: popup.paper,
                    types: [...popup.types] },
                  { onSuccess: () => { setPendingEnabled(null); setPopup(null); } });
                }}>
                {save.isPending ? 'збереження…' : 'зберегти'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
